import { addMilliseconds, endOfDay, endOfWeek, format, parseISO, startOfWeek, parse, getDay, addMinutes, startOfDay } from "date-fns";
import { useStore } from "../../store/store";
import { useSearchParams } from "react-router-dom";
import { CalendarFiltersType, SessionStatus } from "./calendar.types";
import { useUpdateRecord } from "../../api/useUpdateRecord";
import { dateFnsLocalizer, DateLocalizer } from "react-big-calendar";
import pt from "date-fns/locale/pt";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { updateGMeeting } from "../../screens/sessions/sessionsOptions";
import { handleSessionSoftValidation } from "../../screens/reserved-slots/validation/sessionSoftValidation";
import { CalendarView } from "../../store/store.types";
import { SERVICE_CATEGORY_ID_MAPPING } from "./serviceCategoryMapping";

export const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
    getDay,
    locales: { "pt-PT": pt },
});

export const handleDefaultDates = (dateString: string | null, type: string) => {
    const dateFromParams = dateString ? parseISO(dateString) : null;

    const startOfTheWeek = format(startOfWeek(dateFromParams || new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd HH:mm:ss");
    const endOfTheWeek = format(endOfWeek(dateFromParams || new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd HH:mm:ss");

    if (type === "start") return startOfTheWeek;
    if (type === "end") return endOfTheWeek;

    return format(new Date(), "yyyy-MM-dd HH:mm:ss");
};

type CalendarRange =
    | Date[]
    | {
          start: Date;
          end: Date;
      };

export const handleRanges = (range: CalendarRange) => {
    const isArray = Array.isArray(range);

    if (isArray && range.length > 1) {
        return {
            start_date: format(startOfWeek(range[0], { weekStartsOn: 1 }), "yyyy-MM-dd HH:mm:ss"),
            end_date: format(endOfWeek(range[0], { weekStartsOn: 1 }), "yyyy-MM-dd HH:mm:ss"),
        };
    }

    if (isArray && range.length === 1) {
        return {
            start_date: format(range[0], "yyyy-MM-dd HH:mm:ss"),
            end_date: format(endOfDay(range[0]), "yyyy-MM-dd HH:mm:ss"),
        };
    }
    if (!isArray) {
        return {
            start_date: format(range.start, "yyyy-MM-dd HH:mm:ss"),
            end_date: format(range.end, "yyyy-MM-dd HH:mm:ss"),
        };
    }
};

type DefaultFiltersOptions = {
    status?: SessionStatus;
    clinic_id?: number[] | undefined;
    view?: CalendarView;
};

export const useDefaultFilters = (options?: DefaultFiltersOptions) => {
    const [params] = useSearchParams();
    const {
        calendar: { defaultDate },
    } = useStore();
    const {clinic_id, ...restOfOptions} = options || {};

    if (options?.view === "day") {
        const defaultFilters = {
            clinic_id: clinic_id || [1],
            dateRange: handleRanges([startOfDay(new Date())]),
            ...restOfOptions,
        };
        return defaultFilters;
    }

    const defaultFilters = {
        clinic_id: clinic_id || [1],
        dateRange: {
            start_date: handleDefaultDates(params.get("start_date") || defaultDate, "start"),
            end_date: handleDefaultDates(params.get("start_date") || defaultDate, "end"),
        },
        ...restOfOptions,
    };

    return defaultFilters;
};

export type UpdateLocalStateParams = {
    id: string;
    start: Date;
    end: Date;
    resourceId?: number;
};

export const useEventDrop = (
    entity: string,
    refetch?: () => void,
    onLocalUpdate?: (updatedEvent: UpdateLocalStateParams) => void
) => {
    const updateRecord = useUpdateRecord();
    const { notifications } = useStore();

    const onEventChange = async ({ event, start, end, resourceId }: any) => {
        // Store original event data for rollback
        const originalEventData = {
            id: event.id,
            start: event.start,
            end: event.end,
            resourceId: event.resourceId,
        };

            const updateData = {
                start_date: start.toISOString(),
                end_date: end.toISOString(),
            } as any;

            if (resourceId) {
                updateData.room = resourceId;
            }

        try {
            if (onLocalUpdate) {
                onLocalUpdate({ id: event.id, start, end, resourceId });
            }

            const dataUpdated = await updateRecord.mutateAsync({
                id: event.id,
                entity: entity,
                data: updateData,
            });

            if (refetch) {
                refetch();
            }

            if (entity === "sessions" && dataUpdated.meeting_id) {
                const ids = { meeting_id: dataUpdated.meeting_id, id: dataUpdated.id };
                const response = await updateGMeeting(ids, dataUpdated);
                notifications.setNotification({ isOpen: true, message: response?.message, severity: response?.severity });
            }
        } catch (error: any) {
            const rollback = () => onLocalUpdate && onLocalUpdate(originalEventData);

            const wasHandled = await handleSessionSoftValidation(
                error,
                entity,
                async (bypassParams) => {
                    const dataUpdated = await updateRecord.mutateAsync({
                        id: event.id,
                        entity: entity,
                        data: { ...updateData, ...bypassParams },
                    });
                    if (refetch) refetch();
                    if (dataUpdated.meeting_id) {
                        const ids = { meeting_id: dataUpdated.meeting_id, id: dataUpdated.id };
                        const response = await updateGMeeting(ids, dataUpdated);
                        notifications.setNotification({ isOpen: true, message: response?.message, severity: response?.severity });
            }
                },
                rollback
            );
            if (wasHandled) return;

            rollback();
            const errorMessage = error.response.data.errors.map((err: any) => err.message).join(", ");
            notifications.setNotification({ isOpen: true, message: errorMessage, severity: "error" });
        }
    };
    return onEventChange;
};

export const buildServiceCategoryClause = (
    serviceCategory: Array<string>,
    buildCategoryFilter: (categories: Array<string>) => any,
    buildServiceIdFilter: (serviceIds: Array<number>) => any
) => {
    const categories = serviceCategory.filter((value) => !SERVICE_CATEGORY_ID_MAPPING[value]);
    const serviceIds = serviceCategory
        .filter((value) => SERVICE_CATEGORY_ID_MAPPING[value])
        .map((value) => SERVICE_CATEGORY_ID_MAPPING[value]);

    const hasCategoryFilters = categories.length > 0;
    const hasServiceIdFilters = serviceIds.length > 0;

    if (hasCategoryFilters && hasServiceIdFilters) {
        const clause = { _or: [buildCategoryFilter(categories), buildServiceIdFilter(serviceIds)] };
        return clause;
    }
    if (hasCategoryFilters) {
        const clause = buildCategoryFilter(categories);
        return clause;
    }
    if (hasServiceIdFilters) {
        const clause = buildServiceIdFilter(serviceIds);
        return clause;
    }
    return null;
};

export const createCalendarFilter = (calendarFilters: CalendarFiltersType | null) => {
    if (!calendarFilters) return;

    const filters = {
        _and: [
            {
                start_date: {
                    _gte: calendarFilters?.dateRange?.start_date,
                },
            },
            {
                end_date: {
                    _lte: calendarFilters?.dateRange?.end_date,
                },
            },
        ],
    } as any;

    if (calendarFilters.clinic_id && calendarFilters.clinic_id?.length > 0)
        filters._and.push({
            clinic_id: { id: { _in: calendarFilters.clinic_id } },
        });
    if (calendarFilters.service_category && calendarFilters.service_category?.length > 0) {
        const serviceClause = buildServiceCategoryClause(
            calendarFilters.service_category,
            (categories) => ({ service: { category: { _in: categories } } }),
            (serviceIds) => ({ service: { id: { _in: serviceIds } } })
        );

        if (serviceClause) filters._and.push(serviceClause);
    }
    if (calendarFilters.technician && calendarFilters.technician?.length > 0) {
        const isClientOwnerMode = calendarFilters.technicianFilterMode === "client_owner";

        if (isClientOwnerMode) {
            filters._and.push({
                treatment: { client_owner: { id: { _in: calendarFilters.technician } } },
            });
        } else {
            const technicianClauses: Array<any> = [
                { technician: { id: { _in: calendarFilters.technician } } },
                { internal_meetings_participants: { technicians_id: { _in: calendarFilters.technician } } },
            ];

            const hasServiceFilter = calendarFilters.service_category && calendarFilters.service_category.length > 0;
            if (hasServiceFilter) {
                technicianClauses.push({ technician: { _null: true } });
            }

            filters._and.push({ _or: technicianClauses });
        }
    }

    if (calendarFilters.status && calendarFilters.status?.length !== 0)
        filters._and.push({ status: { _in: calendarFilters.status } });

    return filters;
};

export const minTime = new Date();
minTime.setHours(8, 0, 0);

export const maxTime = new Date();
maxTime.setHours(21, 0, 0);

export const scrollToTime = new Date();
scrollToTime.setHours(20, 0, 0, 0);

export const handleCalendarDate = (dateRange: { start_date: string; end_date: string }) => {
    const startDate = parseISO(dateRange.start_date);
    const endDate = parseISO(dateRange.end_date);
    // Calculate the midpoint date of the given range

    const midpointMillis = (endDate.getTime() - startDate.getTime()) / 2;
    const midpointDate = addMilliseconds(startDate, midpointMillis);

    return midpointDate;
};

export type HandleSelectSlotParams = {
    start: Date;
    end?: Date;
    entity: string;
    defaultMinutes: number;
    roomId?: number;
};

export const useHandleSelectSlot = () => {
    const navigate = useNavigate();
    const { schedules: { clinicIds } } = useStore();

    const handleSelectSlot = ({ start, entity, defaultMinutes, roomId, end }: HandleSelectSlotParams) => {

        const startDate = format(start, "yyyy-MM-dd HH:mm", { locale: pt });
        const endDate = end
            ? format(end, "yyyy-MM-dd HH:mm", { locale: pt })
            : format(addMinutes(start, defaultMinutes), "yyyy-MM-dd HH:mm", { locale: pt });
        const roomParam = roomId ? `&[rooms][id]=${roomId}` : "";
        const clinicParam = clinicIds.length === 1 ? `&[clinics][id]=${clinicIds[0]}` : "";

        navigate(`/${entity}/create?start_date=${startDate}&end_date=${endDate}${roomParam}${clinicParam}`);
    };

    return handleSelectSlot;
};

export const createUrl = (params: any, entity: string) => {
    const startDate = params?.data?.start_date;

    const dateFromSession =
        startDate instanceof Date ? startDate : parseISO(params?.recordContext?.item?.start_date || startDate);

    const start_date = format(dateFromSession, "yyyy-MM-dd");
    const clinic_id = params.data.clinic_id || params.recordContext?.item?.clinic_id?.id;

    return `/${entity}?start_date=${start_date}&clinic_id=${clinic_id}`;
};

export const useFormats = (type: "start_date" | "end_date" | "both") => {
    return useMemo(() => {
        return {
            formats: {
                eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }, culture: string, localizer: DateLocalizer) => {
                    if (type === "start_date") return localizer.format(start, "HH:mm", culture);
                    if (type === "end_date") return localizer.format(end, "HH:mm - ", culture);
                    return `${localizer.format(start, "HH:mm", culture)} - ${localizer.format(end, "HH:mm", culture)}`;
                },
            },
        };
    }, []);
};
